export const TABLER_ICON_PREFIX = 'tabler'
export const ICON_PAGE_SIZE = 60
export const DEFAULT_TOP_MENU_ICON = 'tabler:layout-grid'
export const DEFAULT_SUB_MENU_ICON = 'tabler:menu-2'
export const DEFAULT_GENERIC_ICON = 'tabler:help-circle'

const TABLER_ICON_ID_PATTERN = /^tabler:([a-z0-9]+(?:-[a-z0-9]+)*)$/

const COMMON_ICON_DEFINITIONS = [
  { name: 'home', label: '主页', aliases: ['首页', '房屋'] },
  { name: 'layout-dashboard', label: '仪表盘', aliases: ['看板', '工作台'] },
  { name: 'user', label: '用户', aliases: ['人员', '个人'] },
  { name: 'users', label: '用户管理', aliases: ['人员管理', '成员'] },
  { name: 'building', label: '机构', aliases: ['单位', '公司'] },
  { name: 'hierarchy-3', label: '组织架构', aliases: ['部门', '层级'] },
  { name: 'menu-2', label: '菜单', aliases: ['导航', '目录'] },
  { name: 'table', label: '表格', aliases: ['数据表', '列表'] },
  { name: 'settings', label: '设置', aliases: ['配置', '系统设置'] },
  { name: 'bell', label: '通知', aliases: ['提醒', '消息'] },
  { name: 'database', label: '数据库', aliases: ['数据', '存储'] },
  { name: 'file', label: '文件', aliases: ['文档', '资料'] },
  { name: 'folder', label: '文件夹', aliases: ['目录', '资料夹'] },
  { name: 'chart-bar', label: '图表', aliases: ['统计', '报表'] },
  { name: 'shield-lock', label: '权限', aliases: ['安全', '授权'] },
  { name: 'palette', label: '主题', aliases: ['皮肤', '外观'] },
  { name: 'search', label: '搜索', aliases: ['查询', '查找'] },
  { name: 'calendar', label: '日历', aliases: ['日期', '计划'] },
  { name: 'mail', label: '邮件', aliases: ['邮箱', '信件'] },
  { name: 'flask', label: '测试', aliases: ['实验', '调试'] },
]

const commonDefinitionMap = new Map(
  COMMON_ICON_DEFINITIONS.map((definition, index) => [definition.name, { ...definition, rank: index }])
)

let catalogPromise = null

function normalizeSearchText(value) {
  return String(value || '').normalize('NFKC').trim().toLocaleLowerCase()
}

export function formatIconName(name) {
  return String(name || '')
    .split('-')
    .filter(Boolean)
    .join(' ')
}

export function parseTablerIconId(iconId) {
  const match = TABLER_ICON_ID_PATTERN.exec(String(iconId || '').trim())
  return match ? match[1] : null
}

export function isTablerIconId(iconId) {
  return Boolean(parseTablerIconId(iconId))
}

export function normalizeTablerIconId(iconId) {
  const name = parseTablerIconId(iconId)
  return name ? `${TABLER_ICON_PREFIX}:${name}` : ''
}

function buildIconOptions(iconDataByName) {
  return Object.freeze([...iconDataByName.keys()]
    .map((name) => {
      const common = commonDefinitionMap.get(name)
      const value = `${TABLER_ICON_PREFIX}:${name}`
      const englishLabel = formatIconName(name)
      const aliases = common?.aliases || []
      return Object.freeze({
        value,
        name,
        label: common?.label || englishLabel,
        englishLabel,
        aliases,
        commonRank: common?.rank ?? Number.MAX_SAFE_INTEGER,
        searchText: normalizeSearchText([value, englishLabel, common?.label, ...aliases].join(' ')),
      })
    })
    .sort((left, right) => (
      left.commonRank - right.commonRank || left.name.localeCompare(right.name, 'en')
    )))
}

function buildCatalog(iconSet) {
  const collectionWidth = iconSet?.width ?? 16
  const collectionHeight = iconSet?.height ?? 16
  const iconDataByName = new Map(
    Object.entries(iconSet?.icons || {}).map(([name, iconData]) => [
      name,
      Object.freeze({
        ...iconData,
        width: iconData.width ?? collectionWidth,
        height: iconData.height ?? collectionHeight,
      }),
    ])
  )
  let options = null
  return Object.freeze({
    iconDataByName,
    // 菜单显示只需 SVG 数据，选择器第一次搜索时才建立和排序全量搜索项。
    get options() {
      options ||= buildIconOptions(iconDataByName)
      return options
    },
  })
}

export async function loadIconCatalog() {
  if (!catalogPromise) {
    catalogPromise = import('@iconify-json/tabler')
      .then(({ icons }) => buildCatalog(icons))
      .catch((error) => {
        catalogPromise = null
        throw error
      })
  }
  return catalogPromise
}

export function preloadIconCatalog() {
  return loadIconCatalog()
}

export async function iconExists(iconId) {
  const name = parseTablerIconId(iconId)
  if (!name) return false
  const catalog = await loadIconCatalog()
  return catalog.iconDataByName.has(name)
}

export async function resolveIconData(iconId, fallback = DEFAULT_GENERIC_ICON) {
  const catalog = await loadIconCatalog()
  const name = parseTablerIconId(iconId)
  if (name && catalog.iconDataByName.has(name)) {
    return catalog.iconDataByName.get(name)
  }

  const fallbackName = parseTablerIconId(fallback)
  return fallbackName ? catalog.iconDataByName.get(fallbackName) || null : null
}

export async function searchIconOptions(keyword = '', page = 1, pageSize = ICON_PAGE_SIZE) {
  const catalog = await loadIconCatalog()
  const normalizedKeyword = normalizeSearchText(keyword)
  const filteredOptions = normalizedKeyword
    ? catalog.options.filter((option) => option.searchText.includes(normalizedKeyword))
    : catalog.options
  const safePageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : ICON_PAGE_SIZE
  const pageCount = Math.max(1, Math.ceil(filteredOptions.length / safePageSize))
  const requestedPage = Number.isInteger(page) && page > 0 ? page : 1
  const safePage = Math.min(requestedPage, pageCount)
  const startIndex = (safePage - 1) * safePageSize

  return {
    items: filteredOptions.slice(startIndex, startIndex + safePageSize),
    total: filteredOptions.length,
    page: safePage,
    pageSize: safePageSize,
    pageCount,
  }
}
